import React, { useState, useMemo } from 'react';
import { Package } from 'lucide-react';
import { DynamicIcon, iconNames, type IconName } from 'lucide-react/dynamic';

const ALL_ICON_SET = new Set<string>(iconNames);
const LUCIDE_NAME_RE = /^[a-z][a-z0-9-]*$/;

const DEFAULT_ICONS = [
  'wrench', 'car', 'package', 'file-text', 'users', 'building-2', 'briefcase',
  'shopping-cart', 'truck', 'monitor', 'phone', 'mail', 'map-pin', 'calendar',
  'tag', 'bookmark', 'star', 'heart', 'shield', 'zap', 'box', 'layers',
  'database', 'archive', 'clipboard', 'clipboard-check', 'clipboard-list',
  'settings', 'key', 'lock', 'globe', 'printer', 'camera', 'gift', 'headset',
  'calculator', 'check-square', 'cpu', 'smartphone', 'monitor-smartphone',
  'user-plus',
] as const satisfies readonly IconName[];

interface IconPickerProps {
  value: string | null;
  onChange: (icon: string | null) => void;
}

const IconPicker: React.FC<IconPickerProps> = ({ value, onChange }) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo<readonly IconName[]>(() => {
    const q = search.trim().toLowerCase();
    if (!q) return DEFAULT_ICONS;
    return iconNames.filter((n) => n.includes(q)).slice(0, 300);
  }, [search]);

  return (
    <div>
      <input
        className="input mb-2"
        type="text"
        placeholder="Поиск иконки по имени lucide (например, headset, calculator)..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {filtered.length === 0 ? (
        <div
          className="text-xs-gray-500 border-gray-200 rounded-md"
          style={{ padding: 'var(--space-3)', textAlign: 'center' }}
        >
          Ничего не найдено
        </div>
      ) : (
        <div
          className="d-grid gap-1 overflow-auto border-gray-200 rounded-md" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))', maxHeight: 200, padding: '4px' }}
          role="grid"
        >
          {filtered.map((name) => (
            <button
              key={name}
              onClick={() => onChange(value === name ? null : name)}
              title={name}
              aria-label={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                border: value === name ? '2px solid var(--primary-600)' : '1px solid transparent',
                borderRadius: 'var(--radius-md)',
                background: value === name ? 'var(--primary-50)' : 'transparent',
                cursor: 'pointer',
                color: value === name ? 'var(--primary-600)' : 'var(--gray-600)',
                transition: 'all 0.1s',
              }}
            >
              <DynamicIcon name={name} size={18} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default IconPicker;

const EntityIconBase: React.FC<{ name: string | null; size?: number }> = ({
  name,
  size = 18,
}) => {
  if (!name) return <Package size={size} />;
  if (!LUCIDE_NAME_RE.test(name)) {
    return (
      <span
        aria-label={name}
        style={{ fontSize: size, lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size }}
      >
        {name}
      </span>
    );
  }
  if (!ALL_ICON_SET.has(name)) return <Package size={size} />;
  return <DynamicIcon name={name as IconName} size={size} />;
};

export const EntityIcon = React.memo(EntityIconBase);
